import { create } from "zustand";
import { AccountInfo } from "@azure/msal-browser";
import { b2cInstance } from "../auth/b2cInstance";
import { msalInstance } from "../auth/msalInstance";
import { createJSONStorage, persist } from "zustand/middleware";
import { logger } from "../services/logger";

interface AuthStore {
  isB2C: boolean | null;
  token: string | null;
  loginError: string | null;
  activeAccount: AccountInfo | null;
  email: string;
  setEmail: (email: string) => void;
  setToken: (token: string | null) => void;
  setActiveAccount: (account: AccountInfo | null) => void;
  setLoginError: (error: string) => void;
  setAuthType: (isB2C: boolean) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      isB2C: null,
      activeAccount: null,
      token: null,
      loginError: null,
      email: "",
      setEmail: (email) => set({ email }),
      setToken: (token) => set({ token }),
      setActiveAccount: (account) => set({ activeAccount: account }),
      setLoginError: (error) => set({ loginError: error }),
      setAuthType: (isB2C) => set({ isB2C }),
      logout: () => {
        const { isB2C, token } = get();
        try {
          const msalInstanceToUse = isB2C ? b2cInstance : msalInstance;

          const activeAccount = msalInstanceToUse.getActiveAccount();

          if (!activeAccount) {
            window.location.href = `${window.location.origin}/login`;
            localStorage.clear();
            sessionStorage.clear();
            logger.warn("Auth", "No active account found during logout.");
            return;
          }
          msalInstanceToUse.clearCache();

          const logoutRequest = {
            postLogoutRedirectUri: `${window.location.origin}`,
            account: activeAccount,
            logoutHint:
              msalInstanceToUse.getActiveAccount()?.idTokenClaims?.login_hint,
            ...(token ? { idTokenHint: token } : {}),
          };
          if (isB2C) {
            b2cInstance.logoutRedirect(logoutRequest);
          } else {
            msalInstance.logoutRedirect(logoutRequest);
          }
          localStorage.clear();
        } catch (error) {
          logger.error("Auth", "Logout error:", error);
          window.location.href = window.location.origin;
        } finally {
          set({
            activeAccount: null,
            token: null,
            email: "",
            loginError: null,
          });
        }
      },
    }),
    {
      name: "auth-storage",
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        isB2C: state.isB2C,
        activeAccount: state.activeAccount,
        token: state.token,
        email: state.email,
      }),
    }
  )
);
